import Core             from '@/core/Core';
import DemandsV2DAO     from '@/domains/DemandsV2/DemandsV2DAO';
import DemandV2         from '@/domains/DemandsV2/DemandV2';
import DemandRequest    from '@/domains/DemandsV2/DemandRequest';
import DemandOption     from '@/domains/DemandsV2/DemandOption';
import OrdersService    from '@/domains/Orders/OrdersService';
import ContextMenuItem  from '@/lib/components/VContextMenu/ContextMenuItem';
import TabElement       from '@/lib/components/VTabsV2/TabElement';

import type RpcDataPagination               from '@/core/services/Rpc/RpcDataPagination';
import type RpcDataSort                     from '@/core/services/Rpc/RpcDataSort';

class DemandsV2Service {

  /** Общие вкладки для любого пользователя **/
  static readonly COMMON_TABS = [{
    id: 'main',
    name: 'Основное'
  }];

  static readonly SELLERS_TABS = this.COMMON_TABS;

  static readonly BUYERS_TABS = this.COMMON_TABS.concat([{
    id: 'files',
    name: 'Файлы'
  }, {
    id: 'misc',
    name: 'Прочее'
  }]);

  static getTabs() {
    if (Core.currentPartner!.isBuyer) {
      return this.BUYERS_TABS;
    } else {
      return this.SELLERS_TABS;
    }
  }

  /**
   * Вкладки страницы потребностей (/demands-page).
   * Соответствуют статусной модели DemandV2 на беке: комбинации флагов
   * см. DemandV2Registry.getEntityLifeCycleStage.
   * Цвет — индикация стадии, уходит в --active-color активной вкладки
   */
  static readonly PAGE_TABS: TabElement[] = [
    TabElement.from('new-requests',      'Поиск вариантов',  null, false, '#609DDB'),
    TabElement.from('under-review',      'Выбор вариантов',  null, false, '#C47ECA'),
    TabElement.from('awaiting-approval', 'Контроль бюджета', null, false, '#E97A74'),
    TabElement.from('draft',             'Ожидают заказа',   null, false, '#5AB6A6'),
  ];

  /**
   * Стадия жизненного цикла потребности → id вкладки страницы.
   * Стадии считает модель (DemandV2.getStage), вкладки — принадлежность
   * страницы, поэтому сопоставление живёт здесь, а не в модели.
   */
  static readonly STAGE_TAB_IDS: Record<string, string> = {
    [DemandV2.Stages.New]:              'new-requests',
    [DemandV2.Stages.AwaitingReview]:   'under-review',
    [DemandV2.Stages.AwaitingApproval]: 'awaiting-approval',
    [DemandV2.Stages.AwaitingOrder]:    'draft',
  };

  /**
   * Вкладка, на которой находится потребность.
   * null — стадия не соответствует ни одной вкладке (удалённая либо
   * комбинация флагов, которую getStage не разбирает).
   */
  static getStageTab(demandV2: DemandV2): TabElement | null {
    const stage = demandV2.getStage();

    if (stage === null) {
      return null;
    }
    else {
      const tabId = this.STAGE_TAB_IDS[stage];

      if (tabId === undefined) {
        return null;
      }
      else {
        return this.PAGE_TABS.find(tab => tab.id === tabId) ?? null;
      }
    }
  }

  /** ================ Загрузка ================ **/

  /**
   * Загружает список потребностей с фильтрацией.
   * Возвращает гидрированные сущности
   */
  static async getList(
    params?: Parameters<typeof DemandsV2DAO.getList>[0]
  ): Promise<DemandV2[]> {
    const response = await DemandsV2DAO.getList(params);

    if (response.ok === false) return [];
    if (response.isCancelled) return [];

    return Core.utils.hydrateArray(response.data, DemandV2.fromServer);
  }

  /**
   * Загружает список потребностей по контракту DataGridLoader:
   * принимает один объект {фильтры..., pagination, sort},
   * возвращает {data, pagination, cancelled?}
   */
  static async getListPaginated(
    params?: Parameters<typeof DemandsV2DAO.getList>[0] & {
      pagination?: RpcDataPagination | null;
      sort?: RpcDataSort | null;
    },
  ): Promise<{ data: DemandV2[]; pagination: RpcDataPagination | null; cancelled?: boolean }> {
    const { pagination = null, sort = null, ...filters } = params ?? {};

    const response = await DemandsV2DAO.getList(filters, pagination, sort);

    if (response.isCancelled) {
      return { data: [], pagination: null, cancelled: true };
    }

    if (response.ok === false) {
      return { data: [], pagination: null };
    }

    return {
      data:       Core.utils.hydrateArray(response.data, DemandV2.fromServer),
      pagination: response.meta?.pagination ?? null,
    };
  }

  /**
   * Загружает одну потребность по ID (с заявками и вариантами)
   */
  static async get(id: number): Promise<DemandV2 | null> {
    const response = await DemandsV2DAO.get(id);

    if (!response.ok) {
      Core.showNotification.error('Ошибка при загрузке потребности');
      return null;
    }

    return DemandV2.fromServer(response.data) as DemandV2;
  }

  /** ================ CRUD ================ **/

  /**
   * Создаёт новую потребность (заявки уходят каскадом внутри demand.requests)
   */
  static async add(demandV2: DemandV2): Promise<boolean> {
    const response = await DemandsV2DAO.add(demandV2);

    if (response.ok) {
      Core.showNotification.success('Потребность успешно создана');
    }

    return response.ok;
  }

  /**
   * Обновляет потребность. Заявки внутри demand.requests бек игнорирует
   */
  static async update(demandV2: DemandV2, reasonText?: string): Promise<boolean> {
    const response = await DemandsV2DAO.update(demandV2, reasonText);

    if (response.ok) {
      Core.showNotification.success('Потребность успешно обновлена');
    }

    return response.ok;
  }

  /**
   * Удаляет потребность
   */
  static async delete(id: number, reasonText?: string): Promise<boolean> {
    const response = await DemandsV2DAO.delete(id, reasonText);

    if (response.ok) {
      Core.showNotification.success('Потребность удалена');
    } else {
      Core.showNotification.error('Потребность не удалена');
    }

    return response.ok;
  }

  /** Удаление потребности с подтверждением */
  static async deleteWithConfirm(demandV2: DemandV2): Promise<boolean> {
    const { isApproved, reasonText } = await Core.dialogs.confirmWithReason(
      'Удалить эту потребность?',
      `Потребность ${demandV2.id} ${demandV2.name}`,
    );

    if (isApproved === true) {
      const isDeleted = await this.delete(demandV2.id, reasonText);

      if (isDeleted === true) {
        demandV2.isDeleted = true;
      }

      return isDeleted;
    } else {
      return false;
    }
  }

  /** ================ Заявки ================ **/

  /**
   * Создаёт новую заявку. Бек не возвращает id созданной сущности,
   * поэтому после добавления потребность нужно перезапрашивать
   */
  static async addRequest(demandRequest: DemandRequest): Promise<boolean> {
    const response = await DemandsV2DAO.addRequest(demandRequest);

    if (response.ok) {
      Core.showNotification.success('Заявка успешно создана');
    }

    return response.ok;
  }

  /**
   * Обновляет заявку
   */
  static async updateRequest(demandRequest: DemandRequest): Promise<boolean> {
    const response = await DemandsV2DAO.updateRequest(demandRequest);

    if (response.ok) {
      Core.showNotification.success('Заявка успешно обновлена');
    } else {
      Core.showNotification.error('Не удалось обновить заявку');
    }

    return response.ok;
  }

  /**
   * Удаляет заявку
   */
  static async deleteRequest(id: number): Promise<boolean> {
    const response = await DemandsV2DAO.deleteRequest(id);

    if (response.ok) {
      Core.showNotification.success('Заявка успешно удалена');
    } else {
      Core.showNotification.error('Не удалось удалить заявку');
    }

    return response.ok;
  }

  /** Удаление заявки с подтверждением. Возвращает true если удаление произошло. */
  static async deleteRequestWithConfirm(demandRequest: DemandRequest): Promise<boolean> {
    const isConfirmed = await Core.confirm('Подтверждаете удаление запроса?');

    if (isConfirmed) {
      return this.deleteRequest(demandRequest.id);
    } else {
      return false;
    }
  }

  /** ================ Диалог заявок ================ */

  /**
   * Открывает диалог создания заявки
   */
  static createRequest(demandV2: DemandV2): Promise<boolean> {
    const request = new DemandRequest();
    request.demand = demandV2;

    return this.showRequestDialog(request);
  }

  /**
   * Открывает диалог редактирования заявки
   */
  static editRequest(request: DemandRequest): Promise<boolean> {
    return this.showRequestDialog(request);
  }

  /**
   * Показывает диалог заявки.
   * Промис резолвится по закрытию диалога: true — заявку сохранили, false — нет.
   */
  private static showRequestDialog(request: DemandRequest): Promise<boolean> {
    return Core.dialogs.DemandV2RequestDialog.showAsPromise({ request });
  }

  /** ================ Варианты ================ **/

  /**
   * Создаёт новый вариант. Бек не возвращает id созданной сущности,
   * поэтому после добавления потребность нужно перезапрашивать
   */
  static async addOption(demandV2Option: DemandOption): Promise<boolean> {
    const response = await DemandsV2DAO.addOption(demandV2Option);

    if (response.ok) {
      Core.showNotification.success('Вариант успешно создан');
    }

    return response.ok;
  }

  /**
   * Обновляет вариант (в том числе qtySelected — «выбор» варианта идёт через update)
   */
  static async updateOption(demandV2Option: DemandOption): Promise<boolean> {
    const response = await DemandsV2DAO.updateOption(demandV2Option);

    if (response.ok) {
      Core.showNotification.success('Вариант успешно обновлён');
    }

    return response.ok;
  }

  /**
   * Удаляет вариант
   */
  static async deleteOption(id: number): Promise<boolean> {
    const response = await DemandsV2DAO.deleteOption(id);

    if (response.ok) {
      Core.showNotification.success('Вариант успешно удалён');
    }

    return response.ok;
  }

  /** ================ Диалоги вариантов ================ **/

  /**
   * Открывает диалог создания варианта
   */
  static createOption(demand: DemandV2): Promise<boolean> {
    const option = DemandOption.fromDemandV2(demand);

    return this.showOptionDialog(option);
  }

  /**
   * Открывает диалог редактирования варианта
   */
  static editOption(option: DemandOption): Promise<boolean> {
    return this.showOptionDialog(option);
  }

  /**
   * Показывает диалог варианта.
   * Промис резолвится по закрытию диалога: true — что-то сохранили или удалили, false — нет.
   */
  private static showOptionDialog(option: DemandOption): Promise<boolean> {
    return Core.dialogs.DemandV2OptionDialog.showAsPromise({ demandOption: option });
  }

  /** Удаление варианта с подтверждением. Возвращает true если удаление произошло. */
  static async deleteOptionWithConfirm(option: DemandOption): Promise<boolean> {
    const isConfirmed = await Core.confirm('Удалить опцию?');

    if (isConfirmed) {
      return this.deleteOption(option.id);
    } else {
      return false;
    }
  }

  /** Открывает диалог истории сделок с поставщиком варианта. */
  static openOrdersHistoryDialog(option: DemandOption): void {
    Core.dialogs.OrderPositionsRegistryDialog.show({
      title: `История сделок с поставщиком ${option.seller.toString()}`,
      params: {
        seller: { id: option.seller.id },
      },
      hiddenColumns: [
        'comment',
        'orderCategory',
        'seller',
        'buyer',
        'documentNumber',
        'desiredReceiptDate',
        'plannedShipmentDate',
        'payment',
      ],
    });
  }


  /** Промис резолвится по закрытию диалога: true — потребность перенесена, false — нет. */
  static async showMoveDemandDialog(demandV2: DemandV2): Promise<boolean> {
    return Core.dialogs.MoveDemandV2.showAsPromise({
      localDemandV2: demandV2,
    });
  }

  /**
   * Показывает карточку потребности. null — режим создания: реестр зовёт
   * так из кнопки «Создать потребность».
   * Промис резолвится по закрытию карточки: true если закрыта через resolve,
   * иначе false
   */
  static showDemandV2Card(demand: DemandV2 | null): Promise<boolean> {
    return Core.dialogs.DemandV2Card.showAsPromise({ id: demand?.id });
  }

  /**
   * Утверждает/снимает утверждение потребности.
   * После update перечитывает сущность целиком и мутирует переданную
   * с сохранением ссылки (для грида): бек инкрементит version и может
   * сам сбросить isApproved при изменениях в согласованной потребности.
   * Возвращает результат update.
   */
  static async setApproved(demandV2: DemandV2, value: boolean): Promise<boolean> {
    demandV2.isApproved = value;
    const isUpdated = await this.update(demandV2);

    const fresh = await this.get(demandV2.id);
    if (Core.utils.isNullOrUndefined(fresh) === false) {
      Object.assign(demandV2, fresh);
    }

    return isUpdated;
  }

  /**
   * Пункты контекстного меню потребности для грида реестра и карточки.
   * Грид обновляет список, только если callback пункта вернул true; карточка
   * в этом случае перечитывает потребность (onContextMenuAction).
   */
  static get contextMenu(): ContextMenuItem<DemandV2>[] {
    return [
      new ContextMenuItem<DemandV2>(
        'Открыть',
        'open_in_new',
        (demandV2) => { return this.showDemandV2Card(demandV2); },
        null,
        false,  // hidden
        true,   // dividerAfter
      ),
      new ContextMenuItem<DemandV2>(
        'Создать заказы',
        'add_circle_outline',
        async (demandV2) => {
          demandV2.isCreatingOrders = true;
          const isCreated = await OrdersService.createOrdersFromDemandV2(demandV2.id);
          demandV2.isCreatingOrders = false;

          return isCreated;
        },
        (demandV2) => { return demandV2.getReasonCannotCreateOrder(); },
      ),
      new ContextMenuItem<DemandV2>(
        'Добавить вариант',
        'add',
        (demandV2) => { return this.createOption(demandV2); },
        (demandV2) => { return demandV2.getReasonCannotAddOption(); },
      ),
      new ContextMenuItem<DemandV2>(
        'Утвердить',
        'check_circle',
        (demandV2) => { return this.setApproved(demandV2, true); },
        (demandV2) => { return demandV2.getReasonCannotApprove(); },
        false,  // hidden
        true,   // dividerAfter
      ),
      new ContextMenuItem<DemandV2>(
        'Отправить на доработку',
        'replay',
        (demandV2) => { return this.showMoveDemandDialog(demandV2); },
        (demandV2) => { return demandV2.getReasonCannotMove(); },
        false,  // hidden
        true,   // dividerAfter
      ),
      new ContextMenuItem<DemandV2>(
        'Удалить',
        'delete',
        (demandV2) => { return this.deleteWithConfirm(demandV2); },
      ),
    ];
  }

  /**
   * Пункты контекстного меню заявки для грида в карточке потребности
   */
  static get demandRequestContextMenu(): ContextMenuItem<DemandRequest>[] {
    return [
      new ContextMenuItem<DemandRequest>(
        'Открыть',
        'open_in_new',
        (demandRequest) => { return this.editRequest(demandRequest); },
        null,   // disabled
        false,  // hidden
        true,   // dividerAfter
      ),
      new ContextMenuItem<DemandRequest>(
        'Удалить',
        'delete',
        (demandRequest) => { return this.deleteRequestWithConfirm(demandRequest); },
      ),
    ];
  }

  /**
   * Пункты контекстного меню варианта для грида в карточке потребности
   */
  static get demandOptionContextMenu(): ContextMenuItem<DemandOption>[] {
    return [
      new ContextMenuItem<DemandOption>(
        'Открыть',
        'open_in_new',
        (option) => { return this.editOption(option); },
        null,   // disabled
        false,  // hidden
        true,   // dividerAfter
      ),
      new ContextMenuItem<DemandOption>(
        'История сделок с поставщиком',
        'history',
        (option) => { this.openOrdersHistoryDialog(option); },
      ),
      new ContextMenuItem<DemandOption>(
        'Удалить',
        'delete',
        (option) => { return this.deleteOptionWithConfirm(option); },
      ),
    ];
  }

  /**
   * Пункты меню тулбара карточки варианта (DemandV2OptionDialog): без «Открыть» —
   * карточка уже открыта. У несохранённого варианта VDialogV3 меню не показывает
   */
  static get demandOptionCardContextMenu(): ContextMenuItem<DemandOption>[] {
    return [
      new ContextMenuItem<DemandOption>(
        'История сделок с поставщиком',
        'history',
        (option) => { this.openOrdersHistoryDialog(option); },
      ),
      new ContextMenuItem<DemandOption>(
        'Удалить',
        'delete',
        (option) => { return this.deleteOptionWithConfirm(option); },
      ),
    ];
  }
}

export default DemandsV2Service;
